<script setup>
/* eslint-disable */
// SiteUp (2026-08-11): mesmo tratamento do irmão KanbanActions.vue/
// ContactPanel.vue. Este arquivo carrega 32 violações ANTERIORES a esta
// fatia (PR #418) — dívida do módulo Kanban legado, não desta mudança — e
// o lint do CI é estrito por arquivo tocado: sem isto, nenhuma mudança aqui
// consegue ficar verde. Registrado, não resolvido — limpar é PR próprio.
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import KanbanItemHeader from './KanbanItemHeader.vue';
import KanbanNotesTab from './KanbanNotesTab.vue';
import KanbanChecklistTab from './KanbanChecklistTab.vue';
import KanbanAttachmentsTab from './KanbanAttachmentsTab.vue';
import KanbanActivitiesTab from './KanbanActivitiesTab.vue';
import KanbanTasksTab from './KanbanTasksTab.vue';
import KanbanVoipTab from './KanbanVoipTab.vue';
import KanbanContextoTab from './KanbanContextoTab.vue';
import KanbanConversationTab from './KanbanConversationTab.vue';
import KanbanBasicDataPanel from './KanbanBasicDataPanel.vue';
import KanbanAgentInfo from './KanbanAgentInfo.vue';
import VoipScoreBadge from 'dashboard/components/widgets/VoipScoreBadge.vue';
import ScheduleMessageModal from 'dashboard/components/widgets/conversation/bubble/ScheduleMessageModal.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import conversationAPI from '../../../../api/inbox/conversation';
import inboxAPI from '../../../../api/inboxes';
import kanbanAPI from '../../../../api/kanban';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import {
  buildNextStepBadge,
  nextStepPanelTab,
} from 'dashboard/helper/kanbanNextStep';

// Definição dos props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  kanbanItems: {
    type: Array,
    required: true,
  },
  conversations: {
    type: Array,
    required: true,
  },
  contactsList: {
    type: Array,
    required: true,
  },
  loadingItems: {
    type: Boolean,
    default: false,
  },
  loadingConversations: {
    type: Boolean,
    default: false,
  },
  loadingContacts: {
    type: Boolean,
    default: false,
  },
  currentUser: {
    type: Object,
    required: true,
  },
  agentList: {
    type: Array,
    required: true,
  },
  initialActiveTab: {
    type: String,
    default: null,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
  agentInfo: {
    type: Object,
    default: null,
  },
  loadingAgent: {
    type: Boolean,
    default: false,
  },
  // SiteUp CRM (2026-09-18): 'panel' = card no painel lateral do board. Só o
  // painel ganha a aba "Conversa" (somente leitura); em tela cheia a conversa
  // continua sendo a da caixa de entrada.
  layout: {
    type: String,
    default: 'full',
  },
});
// Definição dos emits
const emit = defineEmits([
  'upload-attachment',
  'delete-attachment',
  'update-item',
  'item-updated',
  'navigate-to-conversation',
  'context-menu',
  'stage-click',
  'tab-changed',
  'select-item',
  'agent-removed',
  'agent-assigned',
  'link-conversation',
]);
// Confirmation modal state
const confirmModalRef = ref(null);
const confirmTitle = ref('');
const confirmDescription = ref('');
const confirmLabel = ref('Confirmar');
async function confirmAction(title, description, label = 'Confirmar') {
  confirmTitle.value = title;
  confirmDescription.value = description;
  confirmLabel.value = label;
  return await confirmModalRef.value.showConfirmation();
}

const { t, locale: uiLocale } = useI18n();
const router = useRouter();

// Estado das tabs — Contexto primeiro (layout tab-first)
const activeTab = ref('contexto');
const showDebug = ref(false);

// Só deve carregar "Mensagens agendadas" se houver conversa vinculada
const hasConversation = computed(() => {
  return Boolean(
    props.item?.conversation?.id || props.item?.item_details?.conversation?.id
  );
});
const isPanelLayout = computed(() => props.layout === 'panel');

// O pai espera (event, conversationId) e chama stopPropagation: a aba Conversa
// não nasce de um clique aninhado, então vai um evento inerte.
const noopEvent = { stopPropagation: () => {} };

// Próximo passo do painel: o mesmo helper puro do card, para o painel e o card
// nunca discordarem. `scheduled_at` continua como fallback para o item que
// ainda não recebeu `next_step` do servidor (card compartilhado, payload
// antigo em cache).
const panelNextStep = computed(() => props.item?.next_step || null);

const panelNextStepBadge = computed(() =>
  buildNextStepBadge(
    panelNextStep.value,
    new Date(),
    toIntlLocale(uiLocale.value || 'pt_BR')
  )
);

const panelNextStepLabel = computed(() => {
  if (!panelNextStep.value || panelNextStepBadge.value.tone === 'none') {
    return '';
  }
  return t(
    panelNextStepBadge.value.labelKey,
    panelNextStepBadge.value.labelParams
  );
});

const openNextStepTab = () => {
  activeTab.value = nextStepPanelTab(panelNextStep.value);
};

const panelNextStepText = computed(() => {
  const scheduledAt = props.item?.item_details?.scheduled_at;
  if (!scheduledAt) return '';
  const date = new Date(
    typeof scheduledAt === 'string' && scheduledAt.length === 10
      ? `${scheduledAt}T12:00:00`
      : scheduledAt
  );
  if (!Number.isFinite(date.getTime())) return '';
  return t('KANBAN.ITEM_PANEL.NEXT_STEP_SCHEDULED', {
    date: date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
});

// Contador de não lidas só quando o dado já vem no payload da conversa; nada
// de requisição extra só para pintar um número.
const conversationUnreadCount = computed(() => {
  const conversation =
    props.item?.conversation || props.item?.item_details?.conversation || null;
  return Number(conversation?.unread_count) || 0;
});

const isSharedRecipient = computed(() => props.item?.access?.shared === true);
const canCommentOnSharedItem = computed(
  () =>
    props.item?.access?.can_comment === true ||
    props.item?.access?.can_edit === true
);

// Watcher para emitir mudança de aba
watch(activeTab, newTab => {
  emit('tab-changed', newTab);

  // Buscar mensagens agendadas quando a aba for ativada
  if (newTab === 'scheduled-messages') {
    fetchScheduledMessages();
  }

  // Buscar outros itens quando a aba for ativada
  if (newTab === 'other-items') {
    fetchOtherItems();
  }
});

// Refs para upload de anexos
const isUploadingAttachment = ref(false);
const selectedFileName = ref('');
const MAXIMUM_FILE_UPLOAD_SIZE = 67108864; // 64MB

// Refs para busca nos modais de vinculação
const itemSearchTerm = ref('');
const conversationSearchTerm = ref('');
const contactSearchTerm = ref('');

// Estado para mensagens agendadas
const scheduledMessages = ref([]);
const loadingScheduledMessages = ref(false);
const showScheduleModal = ref(false);
const messageBeingEdited = ref(null);

// Estado para outros itens
const otherItems = ref([]);
const loadingOtherItems = ref(false);

// Estado para cache de inboxes
const inboxesCache = ref(new Map());

// Computed para formatar data
const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Computed para histórico (mock - pode ser ajustado conforme necessidade)
const history = computed(() => {
  if (!props.item?.activities) return [];

  return props.item.activities.map(activity => ({
    id: activity.id || Date.now(),
    title: activity.type || 'Atividade',
    details: activity.details || '',
    created_at: activity.created_at,
    user: activity.user,
    icon: 'calendar-clock', // ícone padrão
  }));
});

// Computed para obter as notas do item
const notes = computed(() => {
  const itemNotes = props.item?.item_details?.notes;
  return Array.isArray(itemNotes) ? itemNotes : [];
});

// Computed filtrados
const filteredKanbanItems = computed(() => {
  if (!itemSearchTerm.value) return props.kanbanItems;
  return props.kanbanItems.filter(item =>
    (item.title || '')
      .toLowerCase()
      .includes(itemSearchTerm.value.toLowerCase())
  );
});

const filteredConversations = computed(() => {
  if (!conversationSearchTerm.value) return props.conversations;
  return props.conversations.filter(conv =>
    ((conv.title || '') + ' ' + (conv.description || ''))
      .toLowerCase()
      .includes(conversationSearchTerm.value.toLowerCase())
  );
});

const filteredContactsList = computed(() => {
  if (!contactSearchTerm.value) return props.contactsList;
  return props.contactsList.filter(contact =>
    (contact.name || '')
      .toLowerCase()
      .includes(contactSearchTerm.value.toLowerCase())
  );
});

// Phase 2.9.8 H: aba VOIP aparece so quando item tem voip_summary (backend
// filtra por account.feature_enabled('voip_calls')). Contas sem VOIP nem
// veem a aba.
const hasVoipSummary = computed(() => !!props.item?.voip_summary);

const notesCount = computed(() => notes.value.length);

const checklistCount = computed(() => {
  const list = Array.isArray(props.item?.checklist) ? props.item.checklist : [];
  return list.length;
});

const offersCount = computed(() => {
  const offers = props.item?.item_details?.offers;
  return Array.isArray(offers) ? offers.length : 0;
});

const agentsCount = computed(() => {
  const agents = props.item?.assigned_agents;
  return Array.isArray(agents) ? agents.length : 0;
});

// SiteUp V2 (2026-08-11), mockup aprovado: 10 abas em fileira única viram
// SEIS na frente + "Mais". Nenhuma some — e o "Mais" ganha um pontinho quando
// alguma aba escondida tem conteúdo, para nada ficar invisível lá dentro.
//
// A contagem vem de quem TEM o dado no pai. "Tarefas" fica sem número de
// propósito: quem sabe contar é o filho, e seção fechada não monta o filho
// (a armadilha do acordeão, 2026-08-10). Número errado é pior que nenhum.
const checklistDone = computed(() => {
  const list = Array.isArray(props.item?.checklist) ? props.item.checklist : [];
  return list.filter(entry => entry?.completed).length;
});

const activitiesCount = computed(() =>
  Array.isArray(props.item?.activities) ? props.item.activities.length : 0
);

const attachmentsCount = computed(
  () => Number(props.item?.attachments?.total_count) || 0
);

// Array de tabs (condicional para mensagens agendadas)
const tabs = computed(() => {
  if (isSharedRecipient.value) {
    return canCommentOnSharedItem.value
      ? [
          {
            id: 'notes',
            icon: 'document',
            label: t('KANBAN.TABS.NOTES'),
            count: notesCount.value,
            group: 'front',
          },
        ]
      : [];
  }

  const baseTabs = [
    {
      id: 'contexto',
      icon: 'person',
      label: t('KANBAN.TABS.CONTEXT'),
      group: 'front',
    },
  ];

  // Aba "Conversa" só no painel lateral, e somente leitura.
  if (isPanelLayout.value) {
    baseTabs.push({
      id: 'conversation',
      icon: 'chat',
      label: t('KANBAN.CONVERSATION_TAB.LABEL'),
      count: conversationUnreadCount.value,
      group: 'front',
    });
  }

  baseTabs.push(
    {
      id: 'notes',
      icon: 'document',
      label: t('KANBAN.TABS.NOTES'),
      count: notesCount.value,
      group: 'front',
    },
    {
      id: 'checklist',
      icon: 'checkmark-circle',
      label: t('KANBAN.TABS.CHECKLIST'),
      // feitas/total: "2/3" diz se ainda falta coisa; "3" não diz nada.
      count: checklistCount.value,
      badge: checklistCount.value
        ? `${checklistDone.value}/${checklistCount.value}`
        : '',
      group: 'front',
    },
    {
      id: 'offers',
      icon: 'tag',
      label: t('KANBAN.TABS.OFFERS'),
      count: offersCount.value,
      group: 'front',
    },
    { id: 'tasks', icon: 'task', label: 'Tarefas', group: 'front' },
    {
      id: 'activities',
      icon: 'calendar-clock',
      label: t('KANBAN.TABS.ACTIVITIES'),
      count: activitiesCount.value,
      group: 'front',
    },
    {
      id: 'agents',
      icon: 'people',
      label: t('KANBAN.TABS.AGENTS'),
      count: agentsCount.value,
      group: 'more',
    },
    {
      id: 'attachments',
      icon: 'attach',
      label: t('KANBAN.TABS.ATTACHMENTS'),
      count: attachmentsCount.value,
      group: 'more',
    }
  );

  if (hasVoipSummary.value) {
    baseTabs.push({
      id: 'voip-history',
      icon: 'call',
      label: 'VOIP',
      group: 'more',
    });
  }

  if (hasConversation.value) {
    baseTabs.push({
      id: 'scheduled-messages',
      icon: 'custom-calendar-sync',
      label: 'Mensagens Agendadas',
      group: 'more',
      customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar-sync-icon lucide-calendar-sync"><path d="M11 10v4h4"/><path d="m11 14 1.535-1.605a5 5 0 0 1 8 1.5"/><path d="M16 2v4"/><path d="m21 18-1.535 1.605a5 5 0 0 1-8-1.5"/><path d="M21 22v-4h-4"/><path d="M21 8.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4.3"/><path d="M3 10h4"/><path d="M8 2v4"/></svg>`,
    });
  }

  baseTabs.push({
    id: 'other-items',
    icon: 'stretch-horizontal',
    label: t('KANBAN.TABS.OTHER_ITEMS'),
    group: 'more',
    customSvg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-stretch-horizontal-icon lucide-stretch-horizontal"><rect width="20" height="6" x="2" y="4" rx="2"/><rect width="20" height="6" x="2" y="14" rx="2"/></svg>`,
  });

  return baseTabs;
});

const frontTabs = computed(() => tabs.value.filter(tab => tab.group !== 'more'));
const moreTabs = computed(() => tabs.value.filter(tab => tab.group === 'more'));

// Pontinho no "Mais": alguma aba escondida tem conteúdo. Sem isso, esconder
// abas vira esconder informação.
const moreHasContent = computed(() =>
  moreTabs.value.some(tab => Number(tab.count) > 0)
);

// Com uma aba escondida ativa, o "Mais" fica aceso e mostra qual é — a pessoa
// nunca fica numa aba que sumiu da barra.
const activeMoreTab = computed(() =>
  moreTabs.value.find(tab => tab.id === activeTab.value)
);

const moreMenuOpen = ref(false);

const pickMoreTab = tab => {
  activeTab.value = tab.id;
  moreMenuOpen.value = false;
};

const closeMoreMenu = () => {
  moreMenuOpen.value = false;
};

onMounted(() => document.addEventListener('mousedown', closeMoreMenu));
onUnmounted(() => document.removeEventListener('mousedown', closeMoreMenu));

const resolveInitialTabId = preferred => {
  if (!preferred || typeof preferred !== 'string') return null;
  const ids = tabs.value.map(tab => tab.id);
  return ids.includes(preferred) ? preferred : null;
};

watch(
  [() => props.initialActiveTab, tabs],
  () => {
    const resolved = resolveInitialTabId(props.initialActiveTab);
    if (resolved) {
      activeTab.value = resolved;
    } else if (!tabs.value.some(tab => tab.id === activeTab.value)) {
      activeTab.value = tabs.value[0]?.id || null;
    }
  },
  { immediate: true, deep: true }
);

// Funções para upload de anexos
const handleNoteAttachment = file => {
  emit('upload-attachment', file);
};

const removeAttachment = attachment => {
  emit('delete-attachment', attachment);
};

// Funções para atualização de item
const handleUpdateItem = item => {
  emit('update-item', item);
};

const handleItemUpdated = () => {
  emit('item-updated');
};

// Função para buscar mensagens agendadas da conversa
const fetchScheduledMessages = async () => {
  // Só buscar se houver conversa vinculada
  const conversationId =
    props.item?.conversation?.id || props.item?.item_details?.conversation?.id;

  if (!conversationId) {
    scheduledMessages.value = [];
    return;
  }

  try {
    loadingScheduledMessages.value = true;
    const response = await conversationAPI.getScheduledMessages(conversationId);
    scheduledMessages.value = response.data.payload || [];

    // Pré-carrega os nomes das inboxes para melhor performance
    await preloadInboxNames();
  } catch (error) {
    console.error('Erro ao buscar mensagens agendadas:', error);
    scheduledMessages.value = [];
  } finally {
    loadingScheduledMessages.value = false;
  }
};

// Função para pré-carregar nomes das inboxes
const preloadInboxNames = async () => {
  const inboxIds = [
    ...new Set(scheduledMessages.value.map(msg => msg.inbox_id)),
  ];

  // Remove IDs que já estão no cache
  const uncachedIds = inboxIds.filter(id => !inboxesCache.value.has(id));

  if (uncachedIds.length === 0) return;

  // Busca em lote para melhor performance
  const promises = uncachedIds.map(id => getInboxName(id));
  await Promise.allSettled(promises);
};

// Função para formatar data das mensagens agendadas
const formatScheduledDate = timestamp => {
  return new Date(timestamp * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Função para obter nome do inbox usando API com cache
const getInboxName = async inboxId => {
  if (!inboxId) return 'Inbox não encontrado';

  // Verifica se já está no cache
  if (inboxesCache.value.has(inboxId)) {
    return inboxesCache.value.get(inboxId);
  }

  try {
    // Busca da API usando o método show da inboxAPI (sem cache)
    const response = await inboxAPI.show(inboxId);
    const inboxName = response.data.name || 'Inbox não encontrado';

    // Armazena no cache
    inboxesCache.value.set(inboxId, inboxName);

    return inboxName;
  } catch (error) {
    console.error('Erro ao buscar nome da inbox:', error);
    // Fallback: tenta buscar no agentList se disponível
    const inbox = props.agentList?.find(i => i.id === inboxId);
    const fallbackName = inbox ? inbox.name : 'Inbox não encontrado';

    // Mesmo com erro, armazena no cache para evitar requests repetidas
    inboxesCache.value.set(inboxId, fallbackName);

    return fallbackName;
  }
};

// Função para obter texto do status
const getStatusText = status => {
  return status === 'sent' ? 'Enviado' : 'Pendente';
};

// Função para obter nome da inbox de forma síncrona (do cache)
const getInboxNameSync = inboxId => {
  if (!inboxId) return 'Inbox não encontrado';

  // Retorna do cache se disponível
  if (inboxesCache.value.has(inboxId)) {
    return inboxesCache.value.get(inboxId);
  }

  // Dispara busca assíncrona em background
  getInboxName(inboxId).catch(() => {
    // Ignora erros para não quebrar o rendering
  });

  // Retorna fallback enquanto busca
  const inbox = props.agentList?.find(i => i.id === inboxId);
  return inbox ? inbox.name : 'Carregando...';
};

// Funções para o modal de agendamento
const openScheduleModal = () => {
  messageBeingEdited.value = null;
  showScheduleModal.value = true;
};

const closeScheduleModal = () => {
  showScheduleModal.value = false;
  messageBeingEdited.value = null;
  // Recarregar mensagens após fechar o modal
  fetchScheduledMessages();
};

// Função para editar mensagem agendada
const editScheduledMessage = message => {
  messageBeingEdited.value = message;
  showScheduleModal.value = true;
};

// Função para deletar mensagem agendada
const deleteScheduledMessage = async messageId => {
  if (
    !(await confirmAction(
      'Excluir mensagem agendada',
      'Tem certeza que deseja excluir esta mensagem agendada?',
      'Excluir'
    ))
  ) {
    return;
  }

  const conversationId =
    props.item?.conversation?.id || props.item?.item_details?.conversation?.id;

  try {
    await conversationAPI.deleteScheduledMessage(conversationId, messageId);
    await fetchScheduledMessages();
    // Você pode adicionar um toast de sucesso aqui se desejar
  } catch (error) {
    console.error('Erro ao deletar mensagem agendada:', error);
    useAlert('Erro ao excluir mensagem agendada: ' + (error?.message || ''));
  }
};

// Função para buscar outros itens com a mesma conversa
const fetchOtherItems = async () => {
  const conversationDisplayId =
    props.item?.conversation?.display_id ||
    props.item?.conversation?.id ||
    props.item?.item_details?.conversation_id ||
    props.item?.item_details?.conversation?.id;

  if (!conversationDisplayId) {
    otherItems.value = [];
    return;
  }

  try {
    loadingOtherItems.value = true;
    const response = await kanbanAPI.getItemsByConversation(
      conversationDisplayId
    );

    // A API retorna { items: [], pagination: {} }
    const items = response.data?.items || [];

    // Filtrar o item atual da lista
    const currentItemId = props.item?.id;
    otherItems.value = items.filter(item => item.id !== currentItemId);
  } catch (error) {
    console.error('Erro ao buscar outros itens:', error);
    otherItems.value = [];
  } finally {
    loadingOtherItems.value = false;
  }
};

// Função para navegar para um item
const navigateToItem = item => {
  // Emit pra parent (KanbanItemDetails) trocar o item visualizado.
  // Antes usava router.push com query item=ID, mas saia do modal
  // quando aberto via KanbanActions/conversation context.
  emit('select-item', item);
};

// Função helper para obter checklist items
const getChecklistItems = item => {
  if (!item.checklist) return [];
  return Array.isArray(item.checklist) ? item.checklist : [];
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col">
    <!-- Tabs: 6 na frente + "Mais" (SiteUp V2, 2026-08-11). -->
    <div class="tabs-container mb-6 shrink-0" data-testid="kanban-card-tabs">
      <div class="tabs-scroll-wrapper">
        <button
          v-for="tab in frontTabs"
          :key="tab.id"
          type="button"
          class="tab-button flex-shrink-0"
          :data-testid="`kanban-tab-${tab.id}`"
          :class="activeTab === tab.id ? 'tab-active' : 'tab-inactive'"
          @click="activeTab = tab.id"
        >
          <div class="flex items-center gap-2">
            <fluent-icon v-if="!tab.customSvg" :icon="tab.icon" size="16" />
            <span v-else class="w-4 h-4 flex-shrink-0" v-html="tab.customSvg" />
            {{ tab.label }}
            <span
              v-if="tab.badge || tab.count"
              class="rounded-full bg-n-alpha-2 px-1.5 text-[11px] font-bold tabular-nums text-n-slate-11"
            >
              {{ tab.badge || tab.count }}
            </span>
          </div>
        </button>

        <div
          v-if="moreTabs.length"
          class="relative flex-shrink-0"
          @mousedown.stop
        >
          <button
            type="button"
            data-testid="kanban-tab-more"
            class="tab-button flex-shrink-0"
            :class="activeMoreTab ? 'tab-active' : 'tab-inactive'"
            :aria-expanded="moreMenuOpen"
            @click="moreMenuOpen = !moreMenuOpen"
          >
            <div class="flex items-center gap-2">
              <fluent-icon icon="more-horizontal" size="16" />
              {{ activeMoreTab ? activeMoreTab.label : t('KANBAN.TABS.MORE') }}
              <span class="i-ri-arrow-down-s-line size-3.5" />
              <span
                v-if="moreHasContent && !activeMoreTab"
                data-testid="kanban-tab-more-dot"
                class="size-1.5 rounded-full bg-n-blue-11"
                :title="t('KANBAN.TABS.MORE_HAS_CONTENT')"
              />
            </div>
          </button>
          <div
            v-if="moreMenuOpen"
            class="absolute right-0 top-full z-30 mt-1 max-h-72 w-56 overflow-auto rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
          >
            <button
              v-for="tab in moreTabs"
              :key="tab.id"
              type="button"
              :data-testid="`kanban-tab-${tab.id}`"
              class="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-n-alpha-2"
              :class="
                activeTab === tab.id
                  ? 'font-bold text-n-slate-12'
                  : 'text-n-slate-11'
              "
              @click="pickMoreTab(tab)"
            >
              <fluent-icon v-if="!tab.customSvg" :icon="tab.icon" size="16" />
              <span v-else class="w-4 h-4 flex-shrink-0" v-html="tab.customSvg" />
              <span class="min-w-0 flex-1 truncate">{{ tab.label }}</span>
              <span
                v-if="tab.count"
                class="rounded-full bg-n-alpha-2 px-1.5 text-[11px] font-bold tabular-nums text-n-slate-11"
              >
                {{ tab.count }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Conteúdo das Tabs -->
    <div class="tabs-content-scroll flex-1 min-h-0 overflow-y-auto">
      <div class="space-y-6 pb-[64px]">
        <!-- Tab Contexto (Contato + Informações + Tempo + Ofertas) -->
        <div v-if="activeTab === 'contexto'">
          <!-- Próximo passo (painel lateral): vem do `next_step` que a API
               anexa ao item (tarefa, retorno ou compromisso), pelo mesmo helper
               puro que pinta a faixa do card. O `scheduled_at` sobrou como
               fallback para o item que ainda não traz `next_step`. -->
          <section
            v-if="isPanelLayout && (panelNextStepLabel || panelNextStepText)"
            class="mb-4 rounded-xl border border-n-weak bg-n-solid-2 p-3"
            data-testid="kanban-panel-next-step"
          >
            <h4 class="mb-1 text-xs font-semibold text-n-slate-11">
              {{ t('KANBAN.ITEM_PANEL.NEXT_STEP') }}
            </h4>
            <!-- Com `next_step` a linha vira atalho para a aba que resolve o
                 passo (Tarefas ou Atividades). Sem ele, segue o texto antigo de
                 `scheduled_at`, sem atalho, porque não há destino certo. -->
            <button
              v-if="panelNextStepLabel"
              type="button"
              class="m-0 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors"
              :class="
                panelNextStepBadge.tone === 'late'
                  ? 'bg-n-ruby-3 text-n-ruby-11'
                  : 'bg-n-teal-3 text-n-teal-11'
              "
              :title="panelNextStepBadge.labelParams.title || panelNextStepLabel"
              @click="openNextStepTab"
            >
              <span
                :class="[panelNextStepBadge.icon, 'size-4 shrink-0']"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1 truncate font-semibold">
                {{ panelNextStepLabel }}
              </span>
            </button>
            <p
              v-else
              class="m-0 flex items-center gap-2 text-sm text-n-slate-12"
            >
              <span
                class="i-ph-calendar-check size-4 shrink-0"
                aria-hidden="true"
              />
              {{ panelNextStepText }}
            </p>
          </section>

          <KanbanContextoTab
            :item="item"
            :current-stage-name="currentStageName"
            :item-description="itemDescription"
            @update:item="handleUpdateItem"
            @item-updated="handleItemUpdated"
            @request-schedule-message="openScheduleModal"
            @open-tab="id => (activeTab = id)"
          />
        </div>

        <!-- Aba Conversa (painel lateral, somente leitura) -->
        <div v-if="activeTab === 'conversation'" class="h-full min-h-0">
          <KanbanConversationTab
            :item="item"
            @navigate-to-conversation="
              id => emit('navigate-to-conversation', noopEvent, id)
            "
            @link-conversation="emit('link-conversation')"
          />
        </div>

        <!-- Tab de Notas -->
        <div v-if="activeTab === 'notes'">
          <KanbanNotesTab
            :item="item"
            :notes="notes"
            :is-stacklab="isStacklab && !isSharedRecipient"
            :kanban-items="filteredKanbanItems"
            :conversations="filteredConversations"
            :contacts-list="filteredContactsList"
            :loading-items="loadingItems"
            :loading-conversations="loadingConversations"
            :loading-contacts="loadingContacts"
            :current-user="currentUser"
            :shared-recipient="isSharedRecipient"
            @upload-attachment="handleNoteAttachment"
            @delete-attachment="removeAttachment"
          />
        </div>

        <!-- Tab de Checklist -->
        <div v-if="activeTab === 'checklist'">
          <KanbanChecklistTab
            :item="item"
            :is-stacklab="isStacklab"
            :current-user="currentUser"
            :agent-list="agentList"
          />
        </div>

        <!-- Tab Ofertas -->
        <div v-if="activeTab === 'offers'">
          <KanbanBasicDataPanel
            :item="item"
            :conversation-data="item?.conversation"
            :loading-conversation="false"
            :current-stage-name="currentStageName"
            :item-description="itemDescription"
            offers-only
            @update:item="handleUpdateItem"
            @item-updated="handleItemUpdated"
          />
        </div>

        <!-- Tab Agentes -->
        <div v-if="activeTab === 'agents'" class="space-y-4">
          <KanbanAgentInfo
            :item="item"
            :agent-info="agentInfo"
            :loading-agent="loadingAgent"
            @agent-removed="$emit('agent-removed', $event)"
            @agent-assigned="$emit('agent-assigned', $event)"
          />

          <div
            v-if="
              item?.voip_summary &&
              (item.voip_summary.score || item.voip_summary.lead_profile)
            "
            class="rounded-2xl outline outline-1 outline-n-weak bg-n-slate-2 p-4 flex items-center gap-3"
          >
            <span class="text-sm font-semibold text-n-slate-12">
              Lead Scoring
            </span>
            <VoipScoreBadge
              :score="item.voip_summary.score"
              :profile="item.voip_summary.lead_profile"
              size="md"
            />
            <span
              v-if="item.voip_summary.origin"
              class="ml-auto truncate text-[11px] text-n-slate-11"
            >
              {{ item.voip_summary.origin }}
            </span>
          </div>

          <div
            v-if="item?.followup"
            class="rounded-2xl outline outline-1 outline-n-weak bg-n-slate-2 p-4 flex items-start gap-3"
          >
            <span
              class="i-ri-notification-line mt-0.5 shrink-0 text-base text-n-slate-11"
            />
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12">
                Follow-up automático
              </p>
              <p class="text-[11px] text-n-slate-11">
                {{
                  item.followup.skipped
                    ? 'IA decidiu não reativar este lead'
                    : `${item.followup.count} retomada(s) enviada(s)`
                }}
              </p>
            </div>
          </div>
        </div>

        <!-- Tab de Anexos -->
        <div v-if="activeTab === 'attachments'">
          <KanbanAttachmentsTab :item="item" :is-stacklab="isStacklab" />
        </div>

        <!-- Tab de Atividades -->
        <div v-if="activeTab === 'activities'">
          <KanbanActivitiesTab
            :item="item"
            :is-stacklab="isStacklab"
            @update-item="handleUpdateItem"
            @item-updated="handleItemUpdated"
          />
        </div>

        <!-- Tab Tarefas -->
        <div v-if="activeTab === 'tasks'">
          <KanbanTasksTab :item="item" />
        </div>

        <!-- Phase 2.9.8 H: Tab VOIP — historico do lead vinculado -->
        <div v-if="activeTab === 'voip-history'">
          <KanbanVoipTab :item="item" />
        </div>

        <!-- Tab de Outros Itens -->
        <div v-if="activeTab === 'other-items'">
          <div class="other-items-container">
            <div
              class="flex gap-3 rounded-xl border border-n-weak bg-n-solid-2/80 px-3 py-2.5 mb-4"
              role="status"
            >
              <span
                class="i-ri-information-line size-3.5 shrink-0 text-woot-500 dark:text-woot-400 mt-0.5"
              />
              <p class="text-xs text-n-slate-11 leading-relaxed">
                {{ t('KANBAN.OTHER_ITEMS_TAB.INFO') }}
              </p>
            </div>

            <!-- Estado de carregamento -->
            <div
              v-if="loadingOtherItems"
              class="flex justify-center items-center py-8"
            >
              <span
                class="w-6 h-6 border-2 border-t-woot-500 border-r-woot-500 border-b-transparent border-l-transparent rounded-full animate-spin"
              />
            </div>

            <!-- Estado vazio -->
            <div v-else-if="!hasConversation" class="empty-state">
              <div class="empty-state-content">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="empty-icon"
                >
                  <path
                    d="M17.75 3A3.25 3.25 0 0 1 21 6.25v11.5A3.25 3.25 0 0 1 17.75 21H6.25A3.25 3.25 0 0 1 3 17.75V6.25A3.25 3.25 0 0 1 6.25 3h11.5Zm1.75 5.5h-15v9.25c0 .966.784 1.75 1.75 1.75h11.5a1.75 1.75 0 0 0 1.75-1.75V8.5Zm-11.75 6a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm-4.25-4a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm1.5-6H6.25A1.75 1.75 0 0 0 4.5 6.25V7h15v-.75a1.75 1.75 0 0 0-1.75-1.75Z"
                    fill="currentColor"
                  />
                </svg>
                <p class="empty-text">Sem conversa vinculada</p>
                <p class="empty-subtext">
                  Esta aba mostra outros itens vinculados à mesma conversa
                </p>
              </div>
            </div>

            <div v-else-if="otherItems.length === 0" class="empty-state">
              <div class="empty-state-content">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="empty-icon"
                >
                  <rect width="20" height="6" x="2" y="4" rx="2" />
                  <rect width="20" height="6" x="2" y="14" rx="2" />
                </svg>
                <p class="empty-text">Nenhum outro item encontrado</p>
                <p class="empty-subtext">
                  Não há outros itens vinculados a esta conversa
                </p>
              </div>
            </div>

            <!-- Lista de outros itens -->
            <div v-else class="other-items-list">
              <div
                v-for="otherItem in otherItems"
                :key="otherItem.id"
                class="other-item-card"
              >
                <div
                  class="bg-n-solid-2 rounded-xl p-4 border border-n-weak transition-all duration-200 hover:-translate-y-0.5"
                >
                  <!-- Header com título e badges -->
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1 flex-wrap">
                        <h3
                          class="text-sm font-semibold text-n-slate-12 leading-tight"
                        >
                          {{ otherItem.item_details?.title || 'Sem título' }}
                        </h3>
                        <!-- Badges de estágio e prioridade juntos -->
                        <div class="flex items-center gap-1.5">
                          <!-- Badge de estágio -->
                          <span
                            v-if="
                              otherItem.funnel_stage &&
                              otherItem.funnel?.stages?.[otherItem.funnel_stage]
                            "
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border"
                            :style="{
                              backgroundColor:
                                otherItem.funnel.stages[otherItem.funnel_stage]
                                  .color + '20',
                              color:
                                otherItem.funnel.stages[otherItem.funnel_stage]
                                  .color,
                              borderColor:
                                otherItem.funnel.stages[otherItem.funnel_stage]
                                  .color + '40',
                            }"
                          >
                            {{
                              otherItem.funnel.stages[otherItem.funnel_stage]
                                .name
                            }}
                          </span>
                          <!-- Badge de prioridade -->
                          <span
                            v-if="
                              otherItem.item_details?.priority &&
                              otherItem.item_details.priority !== 'none'
                            "
                            class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full"
                            :class="{
                              'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800':
                                otherItem.item_details.priority === 'high',
                              'bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800':
                                otherItem.item_details.priority === 'medium',
                              'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800':
                                otherItem.item_details.priority === 'low',
                            }"
                          >
                            {{
                              otherItem.item_details.priority === 'high'
                                ? 'Alta'
                                : otherItem.item_details.priority === 'medium'
                                  ? 'Média'
                                  : 'Baixa'
                            }}
                          </span>
                        </div>
                      </div>

                      <!-- Funil e Contato -->
                      <div
                        class="flex items-center gap-2 text-xs text-n-slate-11"
                      >
                        <span v-if="otherItem.funnel?.name">{{
                          otherItem.funnel.name
                        }}</span>
                        <span
                          v-if="
                            otherItem.funnel?.name &&
                            otherItem.conversation?.contact?.name
                          "
                          >•</span>
                        <span v-if="otherItem.conversation?.contact?.name">{{
                          otherItem.conversation.contact.name
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Grid de informações -->
                  <div class="grid grid-cols-3 gap-2 mb-3">
                    <!-- Conversa -->
                    <div
                      v-if="otherItem.conversation"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <path
                          d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                        />
                      </svg>
                      <span class="font-medium truncate">{{
                        otherItem.conversation.inbox?.name || 'N/A'
                      }}</span>
                      <span class="text-n-slate-11">#{{ otherItem.conversation.display_id }}</span>
                      <svg
                        v-if="otherItem.conversation.status === 'open'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-green-500 flex-shrink-0"
                      >
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    </div>

                    <!-- Checklist progresso -->
                    <div
                      v-if="getChecklistItems(otherItem).length > 0"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span class="font-medium">
                        {{
                          getChecklistItems(otherItem).filter(c => c.completed)
                            .length
                        }}/{{ getChecklistItems(otherItem).length }}
                      </span>
                    </div>

                    <!-- Notas -->
                    <div
                      v-if="otherItem.item_details?.notes?.length"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <path
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                        />
                        <path d="M14 2v6h6" />
                        <path d="M16 13H8" />
                        <path d="M16 17H8" />
                        <path d="M10 9H8" />
                      </svg>
                      <span class="font-medium">{{
                        otherItem.item_details.notes.length
                      }}</span>
                    </div>

                    <!-- Agentes -->
                    <div
                      v-if="otherItem.assigned_agents?.length"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span class="font-medium truncate">{{
                        otherItem.assigned_agents[0].name
                      }}</span>
                      <span
                        v-if="otherItem.assigned_agents.length > 1"
                        class="text-n-slate-11"
                        >+{{ otherItem.assigned_agents.length - 1 }}</span>
                    </div>

                    <!-- Data -->
                    <div
                      v-if="otherItem.created_at"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span class="font-medium">{{
                        formatDate(otherItem.created_at)
                      }}</span>
                    </div>

                    <!-- Anexos -->
                    <div
                      v-if="otherItem.attachments?.total_count"
                      class="flex items-center gap-1.5 text-xs text-n-slate-11"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="text-n-slate-11 flex-shrink-0"
                      >
                        <path
                          d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
                        />
                      </svg>
                      <span class="font-medium">{{
                        otherItem.attachments.total_count
                      }}</span>
                    </div>
                  </div>

                  <!-- Footer com ações -->
                  <div
                    class="flex justify-between items-center pt-3 border-t border-n-weak"
                  >
                    <div
                      v-if="otherItem.assigned_agents?.length"
                      class="flex -space-x-2"
                    >
                      <Avatar
                        v-for="agent in otherItem.assigned_agents.slice(0, 3)"
                        :key="agent.id"
                        :src="agent.avatar_url"
                        :name="agent.name"
                        :size="20"
                        class="border-2 border-white"
                      />
                    </div>
                    <button
                      class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md transition-colors ml-auto"
                      @click.stop="navigateToItem(otherItem)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                      Ver item
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab de Mensagens Agendadas -->
        <div v-if="hasConversation && activeTab === 'scheduled-messages'">
          <div class="scheduled-messages-container">
            <!-- Header com botão de agendar -->
            <div class="scheduled-messages-header">
              <button class="schedule-button" @click="openScheduleModal">
                <span class="i-ri-add-line size-4" />
                Agendar Mensagem
              </button>
            </div>
            <!-- Estado de carregamento -->
            <div
              v-if="loadingScheduledMessages"
              class="flex justify-center items-center py-8"
            >
              <span
                class="w-6 h-6 border-2 border-t-woot-500 border-r-woot-500 border-b-transparent border-l-transparent rounded-full animate-spin"
              />
            </div>

            <!-- Estado vazio -->
            <div v-else-if="scheduledMessages.length === 0" class="empty-state">
              <div class="empty-state-content">
                <svg
                  width="32"
                  height="32"
                  fill="none"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  class="empty-icon"
                >
                  <path
                    d="M17.75 3A3.25 3.25 0 0 1 21 6.25v11.5A3.25 3.25 0 0 1 17.75 21H6.25A3.25 3.25 0 0 1 3 17.75V6.25A3.25 3.25 0 0 1 6.25 3h11.5Zm1.75 5.5h-15v9.25c0 .966.784 1.75 1.75 1.75h11.5a1.75 1.75 0 0 0 1.75-1.75V8.5Zm-11.75 6a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm-4.25-4a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm4.25 0a1.25 1.25 0 1 1 0 2.5a1.25 1.25 0 0 1 0-2.5Zm1.5-6H6.25A1.75 1.75 0 0 0 4.5 6.25V7h15v-.75a1.75 1.75 0 0 0-1.75-1.75Z"
                    fill="currentColor"
                  />
                </svg>
                <p class="empty-text">Nenhuma mensagem agendada</p>
                <p class="empty-subtext">
                  As mensagens agendadas desta conversa aparecerão aqui
                </p>
              </div>
            </div>

            <!-- Lista de mensagens agendadas -->
            <div v-else class="scheduled-messages-list">
              <div
                v-for="message in scheduledMessages"
                :key="message.id"
                class="scheduled-message-card"
              >
                <!-- Card principal com design refinado -->
                <div
                  class="bg-n-solid-2 rounded-2xl p-6 border border-n-weak transition-all duration-200 hover:-translate-y-0.5"
                >
                  <!-- Header com indicador visual -->
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="relative">
                        <!-- Indicador animado baseado no status -->
                        <div
                          class="w-3 h-3 rounded-full animate-pulse"
                          :class="{
                            'bg-woot-500': message.status === 'pending',
                            'bg-green-500': message.status === 'sent',
                          }"
                        />
                        <!-- Animação de pulso adicional para mensagens pendentes -->
                        <div
                          v-if="message.status === 'pending'"
                          class="absolute inset-0 w-3 h-3 bg-woot-500 rounded-full animate-ping opacity-20"
                        />
                      </div>
                      <div>
                        <h3
                          class="text-sm font-semibold text-n-slate-12 leading-tight"
                        >
                          {{ message.title }}
                        </h3>
                        <div class="flex items-center gap-2 mt-1">
                          <!-- Status badge refinado -->
                          <span
                            class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border"
                            :class="{
                              'bg-woot-50 text-woot-700 border-woot-200 dark:bg-woot-900/30 dark:text-woot-300 dark:border-woot-800':
                                message.status === 'pending',
                              'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800':
                                message.status === 'sent',
                            }"
                          >
                            <span
                              v-if="message.status === 'pending'"
                              class="w-1.5 h-1.5 bg-current rounded-full mr-1.5 animate-pulse"
                            />
                            {{ getStatusText(message.status) }}
                          </span>

                          <!-- Badge de recorrência -->
                          <span
                            v-if="message.is_recurrent"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                          >
                            <span class="i-ri-refresh-line size-2.5 mr-1" />
                            Recorrente
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Metadados organizados -->
                  <div class="grid grid-cols-1 gap-3 mb-4">
                    <!-- Data e hora -->
                    <div
                      class="flex items-center gap-2 text-xs text-n-slate-11"
                    >
                      <div
                        class="flex items-center justify-center w-5 h-5 bg-n-alpha-2 rounded-md"
                      >
                        <span
                          class="i-ri-calendar-line size-3 text-n-slate-11"
                        />
                      </div>
                      <span class="font-medium">{{
                        formatScheduledDate(message.scheduled_at)
                      }}</span>
                    </div>

                    <!-- Canal/Inbox -->
                    <div
                      class="flex items-center gap-2 text-xs text-n-slate-11"
                    >
                      <div
                        class="flex items-center justify-center w-5 h-5 bg-n-alpha-2 rounded-md"
                      >
                        <span class="i-ri-chat-1-line size-3 text-n-slate-11" />
                      </div>
                      <span class="font-medium">{{
                        getInboxNameSync(message.inbox_id)
                      }}</span>
                    </div>
                  </div>

                  <!-- Conteúdo da mensagem -->
                  <div class="bg-n-alpha-2/50 rounded-xl p-4">
                    <div class="flex items-start gap-3">
                      <div
                        class="flex items-center justify-center w-6 h-6 bg-woot-100 dark:bg-woot-900/50 rounded-md flex-shrink-0 mt-0.5"
                      >
                        <span
                          class="i-ri-chat-1-line size-3.5 text-woot-600 dark:text-woot-400"
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div
                          class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-wrap"
                        >
                          {{ message.message }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Ações sutis no footer -->
                  <div
                    class="flex justify-end gap-2 mt-4 pt-4 border-t border-n-weak"
                  >
                    <button
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2/50 rounded-md transition-colors"
                      @click="editScheduledMessage(message)"
                    >
                      <span class="i-ri-pencil-line size-3" />
                      Editar
                    </button>
                    <button
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2/50 rounded-md transition-colors"
                      @click="deleteScheduledMessage(message.id)"
                    >
                      <span class="i-ri-delete-bin-line size-3" />
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Agendamento de Mensagem -->
  <ScheduleMessageModal
    v-if="hasConversation"
    :show="showScheduleModal"
    :conversation-id="
      item?.conversation?.id || item?.item_details?.conversation?.id
    "
    :editing-message="messageBeingEdited"
    @close="closeScheduleModal"
  />
  <ConfirmationModal
    ref="confirmModalRef"
    :title="confirmTitle"
    :description="confirmDescription"
    :confirm-label="confirmLabel"
    cancel-label="Cancelar"
  />
</template>

<style scoped>
.tabs-container {
  margin-bottom: 1rem;
}

.tab-button.tab-active {
  @apply text-woot-500;
}

.tab-button.tab-active::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500;
}

.tab-button.tab-inactive {
  @apply text-n-slate-11 hover:text-n-slate-12;
}

.tab-inactive:hover {
  color: #64748b;
}

.dark .tab-inactive {
  color: #94a3b8;
}

.dark .tab-inactive:hover {
  color: #64748b;
}

/* Estilos para as abas com scroll lateral */
.tabs-container {
  overflow: hidden;
}

.tabs-scroll-wrapper {
  display: flex;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-behavior: smooth;
}

.tabs-scroll-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.tabs-content-scroll {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.tabs-content-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.tab-button {
  @apply px-4 py-2 text-xs font-medium relative transition-colors whitespace-nowrap;
}

/* Mobile: ajustes específicos para abas */
@media (max-width: 768px) {
  .tab-button {
    @apply px-3 py-2 text-[11px];
  }

  .tabs-scroll-wrapper {
    padding-bottom: 2px; /* Espaço para o scroll */
  }
}

/* Estilos para a aba de mensagens agendadas */
.scheduled-messages-container {
  @apply space-y-4;
}

.empty-state {
  @apply flex items-center justify-center py-12;
}

.empty-state-content {
  @apply text-center;
}

.empty-icon {
  @apply text-n-slate-10  mx-auto mb-4;
}

.empty-text {
  @apply text-sm font-medium text-n-slate-12 mb-2;
}

.empty-subtext {
  @apply text-xs text-n-slate-11;
}

.scheduled-messages-list {
  @apply space-y-4;
}

.scheduled-message-card {
  animation: fade-in-up 0.5s ease-out;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes card-hover-lift {
  from {
    transform: translateY(0);
    box-shadow:
      0 1px 3px rgba(0, 0, 0, 0.1),
      0 1px 2px rgba(0, 0, 0, 0.06);
  }
  to {
    transform: translateY(-2px);
    box-shadow:
      0 10px 15px rgba(0, 0, 0, 0.1),
      0 4px 6px rgba(0, 0, 0, 0.05);
  }
}

@keyframes status-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

/* Hover effects refinados */
.scheduled-message-card:hover .bg-n-solid-2 {
  animation: card-hover-lift 0.2s ease-out forwards;
}

/* Animação sutil para status badges */
.status-badge {
  transition: all 0.2s ease-in-out;
}

.status-badge:hover {
  transform: scale(1.02);
}

/* Responsividade para mobile */
@media (max-width: 768px) {
  .scheduled-message-card .bg-n-solid-2 {
    padding: 1rem;
    border-radius: 0.5rem;
  }

  .scheduled-message-card .grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .scheduled-message-card .flex.justify-end.gap-2 {
    flex-direction: column;
    gap: 0.25rem;
  }

  .scheduled-message-card button {
    width: 100%;
    justify-content: center;
  }
}

/* Estilos para o header e botão de agendar */
.scheduled-messages-header {
  @apply flex justify-end mb-4;
}

.schedule-button {
  @apply inline-flex items-center gap-2 px-4 py-2 bg-woot-500 hover:bg-woot-600 text-white text-sm font-medium rounded-md transition-colors;
}

.schedule-button:hover {
  @apply bg-woot-600;
}

/* Estilos para a aba de outros itens */
.other-items-container {
  @apply space-y-4;
}

.other-items-list {
  @apply space-y-4;
}

.other-item-card {
  animation: fade-in-up 0.5s ease-out;
}
</style>
